import { useEffect, useLayoutEffect, useRef, useState } from "react"

import { useParams } from "react-router-dom"
import { useAppDispatch, useAppSelector } from "shared/config/store"
import styled from "styled-components"
import classNames from "classnames"
import { AiMessage } from "features/ai-assistant/ui/AiMessage"
import { AiMessageWriter } from "features/ai-assistant/ui/AiMessageWriter"
import { AiMessageLoader } from "features/ai-assistant/ui/AiMessageLoader"
import { selectCurrentChatMessages, selectShouldShowLoadingMessage, selectChatsIdsToShowAnimatedMessage } from "features/ai-assistant/model/selectors/aiAssistantSelectors"
import { actions, fetchChatInfoThunk, fetchChatMessagesThunk, stopPollingThunk } from "features/ai-assistant/model/slice/aiAssistantSlice"
import { MyMessage } from "features/ai-assistant/ui/MyMessage"

export const AiAssistantChatPage = () => {
  const { chatId } = useParams<{ chatId: string }>()
  const dispatch = useAppDispatch()
  const messages = useAppSelector((state) => selectCurrentChatMessages(state, chatId || ''))
  const shouldShowLoadingMessage = useAppSelector((state) => selectShouldShowLoadingMessage(state, chatId || ''))
  const chatsIdsToShowAnimatedMessage = useAppSelector(selectChatsIdsToShowAnimatedMessage)

  const chatWrapperRef = useRef<HTMLDivElement>(null)

  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    if (chatId) {
      setIsVisible(false)
      dispatch(fetchChatInfoThunk({ chatId }))
      dispatch(fetchChatMessagesThunk({ chatId }))
    }

    return () => {
      dispatch(stopPollingThunk())
    }
  }, [chatId, dispatch])

  useLayoutEffect(() => {
    if (chatWrapperRef.current && (messages.length > 0 || shouldShowLoadingMessage)) {
      setTimeout(() => {
        chatWrapperRef.current.scrollTop = chatWrapperRef.current.scrollHeight
        setIsVisible(true)
      }, 50)
    }
  }, [messages, shouldShowLoadingMessage])

  const handleAnimationEnd = () => {
    dispatch(actions.removeChatIdFromShowAnimatedMessage(chatId))
  }

  return (
    <SPageWrapper>
      <SChatWrapper ref={chatWrapperRef}
        className={classNames({ hidden: !isVisible })}
      >
        <SMessagesWrapper>
          {messages.map((message, index) => (
            message.author_id > 0 ? (
              <MyMessage key={message.id}
                message={message}
              />
            ) :
              (
                <AiMessage
                  key={message.id}
                  messageId={message.id}
                  message={message.content}
                  animated={chatsIdsToShowAnimatedMessage.includes(chatId || '') && index === messages.length - 1}
                  onAnimationEnd={handleAnimationEnd}
                  created={message.timestamp}
                  attachments={message.attachments}
                />
              )
          ))}

          {shouldShowLoadingMessage && (
            <SMessageContainer>
              <SMessage>
                <AiMessageLoader />
              </SMessage>
            </SMessageContainer>
          )}
        </SMessagesWrapper>
      </SChatWrapper>

      <SMessageWriterContainer>
        <SMessageWriter btnDisabled={shouldShowLoadingMessage} />
      </SMessageWriterContainer>
    </SPageWrapper>
  )
}

const SPageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  height: 100%;

  * {
    user-select: text;
  }
`

const SChatWrapper = styled.div`
  flex: 1;
  width: 100%;
  padding-right: 4px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: center;
  max-height: calc(100vh - 270px);
  overflow: auto;

  @supports (-moz-appearance: none) { 
    scrollbar-color: var(--scroll-color) transparent; 
  }
  
  &::-webkit-scrollbar {
    width: 4px;
    position: absolute;

    :hover{
      cursor: pointer;
    }
  }

  &::-webkit-scrollbar-thumb {
    background-color: gray;
    border-radius: 14px;

    :hover{
      cursor: pointer;
    }
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &.hidden {
    opacity: 0;
  }
`

const SMessagesWrapper = styled.div`
  margin-top: auto;
  display: flex;
  flex-direction: column;
  gap: 40px;
  width: 804px;
`

const SMessageWriterContainer = styled.div`
  margin-top: auto;
  width: 804px;
`

const SMessageWriter = styled(AiMessageWriter)`
  margin-top: auto;
  width: 800px;
`

const SMessageContainer = styled.div`
  padding-right: 56px;
`

const SMessage = styled.div`
  padding: 16px;
  border-radius: 16px;
  background-color: #F5F5F5;
  color: #000000E0;
  font-size: 14px;
  line-height: 22px;
  font-weight: 400;
`
